import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
	Component,
	computed,
	inject,
	Inject,
	OnDestroy,
	OnInit,
	signal,
} from '@angular/core';
import {
	FormBuilder,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { BaseDialogComponent } from 'app/components/base-dialog/base-dialog.component';
import { IDialogOutput } from 'app/core/interface/dialog.interface';
import {
	AccountClient,
	AddUserDto,
	RoleDto,
	UserInviteTypeEnum,
	UserListSettingsDto,
} from 'app/core/services/api.service';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { ValidateFormService } from 'app/core/services/validate-form.service';
import { INewUserForm } from 'app/modules/settings/users/new-user/new-user-dialog.component';
import { SortArrayPipe } from 'app/shared/pipes/sort-array.pipe';
import { MultiTenantStore } from 'app/store/multi-tenant.store';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'cm-add-tenant',
	animations: fuseAnimations,
	imports: [
		BaseDialogComponent,
		CdkDrag,
		CdkDragHandle,
		FuseAlertComponent,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatOptionModule,
		MatButtonModule,
		SortArrayPipe,
		MatRadioModule,
		MatIconModule,
	],
	templateUrl: './add-tenant.component.html',
})
export class AddTenantComponent implements OnInit, OnDestroy {
	newUserForm: INewUserForm;

	roles = signal<RoleDto[]>([]);
	roles$ = computed(() =>
		this.roles().filter(
			(role) =>
				role.isSystem === true && role.name.toLowerCase() !== 'owner'
		)
	);

	alert: { type: FuseAlertType; message: string } = {
		type: 'success',
		message: '',
	};
	showAlert = false;

	private readonly _multiTenantStore = inject(MultiTenantStore);
	private readonly _unsubscribeAll = new Subject<any>();

	constructor(
		@Inject(MAT_DIALOG_DATA) public config: any,
		private _dialogRef: MatDialogRef<AddTenantComponent>,
		private _formBuilder: FormBuilder,
		private _validateForm: ValidateFormService,
		private _accountClient: AccountClient,
		private _notificationService: NotificationService
	) {
		this.newUserForm = this._formBuilder.group({
			inviteType: this._formBuilder.nonNullable.control(
				UserInviteTypeEnum.External,
				[Validators.required]
			),
			firstName: this._formBuilder.nonNullable.control('', [
				Validators.required,
			]),
			lastName: this._formBuilder.nonNullable.control('', [
				Validators.required,
			]),
			email: this._formBuilder.nonNullable.control('', [
				Validators.required,
				Validators.email,
			]),
			role: this._formBuilder.nonNullable.control('', [
				Validators.required,
			]),
			skipEmail: this._formBuilder.control(false),
		});
	}

	ngOnInit(): void {
		this._setupDialogListeners();
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	/**
	 * Sets up listeners for dialog events (keydown, backdrop click, after open).
	 */
	private _setupDialogListeners(): void {
		this._dialogRef
			.keydownEvents()
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: (event) => {
					if (event.key === 'Escape') {
						this.onCancel();
					}
				},
			});

		this._dialogRef
			.backdropClick()
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: () => this.onCancel(),
			});

		this._dialogRef
			.afterOpened()
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: () => {
					this.roles.set(this.config.roles);
					this.newUserForm
						.get('inviteType')
						.setValue(UserInviteTypeEnum.External);
				},
			});
	}

	/**
	 * Cancels the dialog and closes it with a cancel status.
	 */
	onCancel(): void {
		const output: IDialogOutput = {
			status: 'onCancel',
		};
		this._dialogRef.close(output);
	}

	/**
	 * Handles the save action for the new tenant form.
	 * Validates the form, sends the invite, and handles success/error.
	 */
	onSave(): void {
		if (this.newUserForm.invalid) {
			this._validateForm.execute(this.newUserForm);
			return;
		}

		this.showAlert = false;
		const formData = AddUserDto.fromJS(this.newUserForm.value);

		this._accountClient
			.addExternalUser(formData)
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: (value) => {
					const data = UserListSettingsDto.fromJS(value);
					const output: IDialogOutput = {
						status: 'onSuccess',
						data,
					};
					this._notificationService.open({
						title: 'Invite sent!',
						subtitle: 'Invite has been sent to the user.',
						type: 'success',
					});
					this._dialogRef.close(output);
				},
				error: (err) => {
					const { title } = err;
					this._notificationService.open({
						title: 'An error occurred!',
						subtitle: title || 'Please try again later.',
						type: 'error',
					});
					this.alert = {
						type: 'error',
						message:
							title ||
							'An error occurred. Please try again later.',
					};
					this.showAlert = true;
				},
			});
	}
}
